/**
 * Per-engine `intersects_polygon` predicate builder ("in area" spatial filter — the drawn-polygon
 * analogue of the bbox/distance builders in `spatialFilterEmit.ts`, which is at the 200-line cap).
 *
 * The ring arrives as `condition.polygon` (canonical CCW-open, lng-lat) but this module re-runs
 * `validatePolygonRing` as defense-in-depth — a hand-edited session or any non-UI caller can never
 * push an invalid ring into SQL. Each builder CLOSES the ring (repeats the first vertex) as every
 * engine's polygon literal requires, and assembles predicates from NUMBERS only (via `num`), never
 * interpolating user text. Verified against live PostGIS/MySQL/Oracle/SQL Server (Codex xhigh
 * review): PostGIS uses `ST_GeomFromText`+`ST_Transform(ST_SRID(g))` — NOT the non-existent
 * `ST_GeomForceRHR`; PostGIS geography builds a `ST_GeogFromText` polygon (no transform, SRID 4326);
 * SQL Server emits lng-lat WKT for BOTH geometry and geography (the driver's geography lat-first
 * storage is a READ concern, normalized in `mssqlSpatialMarker.ts`); geography needs the CCW ring
 * (left-hand rule), which `validatePolygonRing` guarantees.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialFilterContext } from '../../core/types/workspaceTypes';
import { makeSpatialFilterUnsupportedError, num, mssqlSrid } from './spatialFilterEmit';
import { validatePolygonRing, type PolygonRing } from './spatialPolygonRing';

/** `"lng lat, lng lat, …, firstLng firstLat"` — the closed WKT ring body (space-separated pairs). */
function wktRingBody(ring: PolygonRing, engine: DatabaseEngineId): string {
  const closed = [...ring, ring[0]];
  return closed.map(([lng, lat]) => `${num(lng, engine)} ${num(lat, engine)}`).join(', ');
}

/** `"lng,lat,lng,lat,…,firstLng,firstLat"` — the closed Oracle SDO_ORDINATE_ARRAY body. */
function ordinateBody(ring: PolygonRing, engine: DatabaseEngineId): string {
  const closed = [...ring, ring[0]];
  return closed.map(([lng, lat]) => `${num(lng, engine)},${num(lat, engine)}`).join(',');
}

/**
 * Build the `intersects_polygon` WHERE fragment for `ring` against the geometry column `g`.
 * Throws the tagged unsupported error for engines/kinds with no verified form (the chip UI hides
 * those via `canEmitSpatialSubOperator`, so the throw is a defense-in-depth backstop) and for a
 * ring that fails validation.
 */
export function buildPolygonSql(
  g: string,
  ctx: SpatialFilterContext,
  polygon: readonly [number, number][],
  engine: DatabaseEngineId,
): string {
  const validated = validatePolygonRing(polygon);
  if (!validated.ok) throw makeSpatialFilterUnsupportedError(engine);
  const ring = validated.ring;

  switch (engine) {
    case 'pgsql': {
      // geography → build a 4326 geography polygon directly (no transform); geometry → build a 4326
      // geometry polygon and reproject to the column's own SRID per row (ST_SRID(g)) so a mixed- or
      // unknown-SRID column can't embed a wrong/NaN literal (mirrors the bbox path, Codex HIGH-3).
      if (ctx.kind === 'geography') {
        return `ST_Intersects(${g}, ST_GeogFromText('POLYGON((${wktRingBody(ring, engine)}))'))`;
      }
      const poly = `ST_GeomFromText('POLYGON((${wktRingBody(ring, engine)}))', 4326)`;
      return `ST_Intersects(${g}, ST_Transform(${poly}, ST_SRID(${g})))`;
    }
    case 'mysql':
      // 4326 + explicit long-lat axis order (SRID 4326 defaults to lat-long); ST_Intersects is the
      // exact-shape predicate (MBRIntersects would reintroduce a bbox false-positive).
      return `ST_Intersects(${g}, ST_GeomFromText('POLYGON((${wktRingBody(ring, engine)}))', 4326, 'axis-order=long-lat'))`;
    case 'oracle':
      // Straight-line exterior polygon (1003,1); CCW exterior + closed ordinate array (both from
      // validatePolygonRing). Tolerance 0.05 matches the existing bbox/distance emit.
      return (
        `SDO_GEOM.RELATE(${g}, 'ANYINTERACT', ` +
        `SDO_GEOMETRY(2003,4326,NULL,SDO_ELEM_INFO_ARRAY(1,1003,1),SDO_ORDINATE_ARRAY(${ordinateBody(ring, engine)})), 0.05) = 'TRUE'`
      );
    case 'mssql': {
      const type = ctx.kind === 'geography' ? 'geography' : 'geometry';
      const srid = mssqlSrid(ctx.srid, engine);
      return `${g}.STIntersects(${type}::STGeomFromText('POLYGON ((${wktRingBody(ring, engine)}))', ${srid})) = 1`;
    }
    default:
      // sqlite (SpatiaLite optional / bbox-only today), kusto, mock — no verified polygon form.
      throw makeSpatialFilterUnsupportedError(engine);
  }
}
